"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Broadcast = void 0;
const log_node_1 = require("@video/log-node");
const mediasoup_client_1 = require("mediasoup-client");
const mobx_1 = require("mobx");
const errors_deprecated_1 = require("./errors-deprecated");
const context_1 = require("./utils/context/context");
const event_emitter_1 = require("./utils/events/event-emitter");
class Broadcast extends event_emitter_1.ObservableEventEmitter {
    constructor(ctx, call, pvcCall, ctrl, broadcastOptions) {
        var _a;
        super();
        this.state = "active";
        this.lastTrafficTS = 0;
        this.lastTrafficBitrate = [];
        (0, mobx_1.makeObservable)(this, {
            state: mobx_1.observable,
        });
        const stream = pvcCall === null || pvcCall === void 0 ? void 0 : pvcCall.streams[broadcastOptions.streamName];
        if (pvcCall == null || stream == null) {
            throw new errors_deprecated_1.StreamNotFoundError("stream not found", {
                call,
                broadcast: this,
            });
        }
        this.ctx = ctx;
        this.ctrl = ctrl;
        this.call = call;
        this.pvcCall = pvcCall;
        this.stream = stream;
        this.options = broadcastOptions;
        if (this.options.simulcast) {
            this.options.videoProducerOptions = (_a = this.options.videoProducerOptions) !== null && _a !== void 0 ? _a : {};
            this.options.videoProducerOptions.simulcast = this.options.simulcast;
        }
        // todo: uncomment after the sfu or transcoder can automatically detect vp8 and transcode it
        // if (!this.options.videoProducerOptions?.codec) {
        //   if (!this.options.videoProducerOptions) {
        //     this.options.videoProducerOptions = {}
        //   }
        //   if (device.isAndroidDevice) {
        //     this.options.videoProducerOptions.codec = H264_CAPABILITY;
        //   } else {
        //     this.options.videoProducerOptions.codec = VP8_CAPABILITY;
        //   }
        // }
        if (this.call.isOwner) {
            this.lastTrafficTS = Date.now();
        }
        ctrl.on("audioDeviceChanged", this.onAudioDeviceChanged);
        ctrl.on("videoDeviceChanged", this.onVideoDeviceChanged);
        ctrl.on("audioMuted", this.onAudioMuted);
        ctrl.on("videoPaused", this.onVideoPaused);
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()");
        this.sourceUpdate().catch((err) => {
            this.emitErrorDeprecated(new errors_deprecated_1.UpdateBroadcastError("initial source update error", {
                broadcast: this,
                call: this.call,
                inner: (0, errors_deprecated_1.wrapNativeError)(err),
            }));
        });
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`Broadcast Class Context Cancelled: ${reason}`));
        this.addInnerDisposer(() => {
            try {
                this.stop();
            }
            catch (err) {
                this.ctx.logger.warn("unable to stop broadcast while disposing", { err: `${err}` });
            }
            if (this.options.disposeController) {
                this.ctrl.dispose("Broadcast options are set to disposeController");
            }
        });
    }
    get streamName() {
        return this.options.streamName;
    }
    get audioMuted() {
        return this.ctrl.audioMuted;
    }
    set audioMuted(value) {
        this.ctrl.audioMuted = value;
    }
    get videoHidden() {
        return this.ctrl.videoPaused;
    }
    set videoHidden(value) {
        this.ctrl.videoPaused = value;
    }
    pause() {
        if (this.ctrl.audioDeviceId != null) {
            this.stream.pauseAudio();
        }
        if (this.ctrl.videoDeviceId != null) {
            this.stream.pauseVideo();
        }
        this.state = "paused";
    }
    get controller() {
        return this.ctrl;
    }
    replaceController(ctrl) {
        this.ctrl = ctrl;
    }
    resume() {
        if (this.ctrl.audioDeviceId != null) {
            this.stream
                .enableAudio(this.options.audioProducerOptions)
                .then(() => {
                this.state = "active";
            })
                .catch((err) => {
                this.emitErrorDeprecated(new errors_deprecated_1.UpdateBroadcastError("unable to resume audio device", {
                    broadcast: this,
                    call: this.call,
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                }));
            });
        }
        if (this.ctrl.videoDeviceId != null) {
            this.stream
                .enableVideo(this.options.videoProducerOptions)
                .then(() => {
                this.state = "active";
            })
                .catch((err) => {
                this.emitErrorDeprecated(new errors_deprecated_1.UpdateBroadcastError("unable to resume video device", {
                    broadcast: this,
                    call: this.call,
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                }));
            });
        }
    }
    stop() {
        this.ctx.logger.debug("stop()");
        this.state = "closed";
        this.stream.close();
        this.ctrl.off("source", this.sourceUpdate);
        this.ctrl.off("audioDeviceChanged", this.onAudioDeviceChanged);
        this.ctrl.off("videoDeviceChanged", this.onVideoDeviceChanged);
        this.ctrl.off("audioMuted", this.onAudioMuted);
        this.ctrl.off("videoPaused", this.onVideoPaused);
        this.pvcCall.removeStream(this.stream.streamName);
    }
    async sourceUpdate() {
        var _a, _b;
        this.ctx.logger.debug("sourceUpdate()", {
            audio: this.ctrl.audioDeviceId,
            video: this.ctrl.videoDeviceId,
        });
        if (this.ctrl.videoDeviceId != null &&
            !this.ctrl.videoPaused &&
            !this.options.audioOnly &&
            !this.ctrl.videoDisabled) {
            // save if the call is closed/closing before entering try/catch block
            const closed = this.pvcCall._closed || ((_a = this.pvcCall.peer) === null || _a === void 0 ? void 0 : _a._closed);
            try {
                await this.pvcCall.enableVideo(this.stream.streamName, true, false, this.options.videoProducerOptions);
            }
            catch (err) {
                const currentlyClosed = this.pvcCall._closed || ((_b = this.pvcCall.peer) === null || _b === void 0 ? void 0 : _b._closed);
                //if the call was closed during async request sallow error
                if (!closed && currentlyClosed) {
                    return;
                }
                this.emitErrorDeprecated(new errors_deprecated_1.EnableVideoError("unable to enable video on `source` event", {
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else if (this.options.audioOnly) {
            this.ctx.logger.debug("Broadcast is set to audioOnly, video not produced", { options: this.options });
        }
        if (this.ctrl.audioDeviceId != null && !this.ctrl.audioMuted && !this.options.videoOnly) {
            try {
                await this.pvcCall.enableAudio(this.stream.streamName, true, false, this.options.audioProducerOptions);
            }
            catch (err) {
                this.emitErrorDeprecated(new errors_deprecated_1.EnableAudioError("unable to enable audio on `source` event", {
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else if (this.options.videoOnly) {
            this.ctx.logger.debug("Broadcast is set to videoOnly, audio not produced", { options: this.options });
        }
    }
    async onAudioDeviceChanged(device) {
        this.ctx.logger.debug("audioDeviceChanged()", { device });
        if (device == null) {
            try {
                await this.pvcCall.disableAudio(this.stream.streamName);
            }
            catch (err) {
                if (err instanceof mediasoup_client_1.types.InvalidStateError && err.message === "track ended") {
                    this.ctx.logger.warn("broadcast has not been updated because the audio track is ended");
                    return;
                }
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.DisableAudioError("unable to disable audio on `audioDeviceChanged` event", {
                    inner,
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else {
            try {
                await this.pvcCall.enableAudio(this.stream.streamName, true, false, this.options.audioProducerOptions);
            }
            catch (err) {
                if (err instanceof mediasoup_client_1.types.InvalidStateError && err.message === "track ended") {
                    this.ctx.logger.warn("broadcast has not been updated because the audio track is ended");
                    return;
                }
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.EnableAudioError("unable to enable audio on `audioDeviceChanged` event", {
                    inner,
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
    }
    async onVideoDeviceChanged(device) {
        this.ctx.logger.debug("videoDeviceChanged()", { device });
        // await this.ctrl.source();
        if (device == null) {
            try {
                await this.pvcCall.disableVideo(this.stream.streamName);
            }
            catch (err) {
                if (err instanceof mediasoup_client_1.types.InvalidStateError && err.message === "track ended") {
                    this.ctx.logger.warn("broadcast has not been updated because the video track is ended");
                    return;
                }
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.DisableVideoError("unable to disable video on `videoDeviceChanged` event", {
                    inner,
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else {
            try {
                await this.pvcCall.enableVideo(this.stream.streamName, true, false, this.options.videoProducerOptions);
            }
            catch (err) {
                if (err instanceof mediasoup_client_1.types.InvalidStateError && err.message === "track ended") {
                    this.ctx.logger.warn("broadcast has not been updated because the video track is ended");
                    return;
                }
                this.emitErrorDeprecated(new errors_deprecated_1.EnableVideoError("unable to enable video on `videoDeviceChanged` event", {
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
    }
    async onAudioMuted(value) {
        this.ctx.logger.debug("audioMuted()", { value });
        if (!this.ctrl.hasActiveAudioTrack()) {
            return;
        }
        if (value) {
            try {
                await this.pvcCall.muteAudio(this.stream.streamName);
            }
            catch (err) {
                this.emitErrorDeprecated(new errors_deprecated_1.EnableAudioError("unable to mute audio on `audioMuted` event", {
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else {
            try {
                await this.pvcCall.unmuteAudio(this.stream.streamName, this.options.audioProducerOptions);
            }
            catch (err) {
                this.emitErrorDeprecated(new errors_deprecated_1.DisableAudioError("unable to unmute audio on `audioMuted` event", {
                    inner: (0, errors_deprecated_1.wrapNativeError)(err),
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
    }
    async onVideoPaused(value) {
        this.ctx.logger.debug("videoPaused()", { value });
        if (!this.ctrl.hasActiveVideoTrack()) {
            return;
        }
        if (value) {
            try {
                await this.pvcCall.pauseVideo(this.stream.streamName);
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.DisableVideoError("unable to pause video on `videoPaused` event", {
                    inner,
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
        else {
            try {
                await this.pvcCall.unpauseVideo(this.stream.streamName, this.options.videoProducerOptions);
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.EnableVideoError("unable to unpause video on `videoPaused` event", {
                    inner,
                    broadcast: this,
                    call: this.call,
                }));
            }
        }
    }
    async hotswapProducer(kind, producerOptions) {
        this.ctx.logger.debug("hotswapProducer()", { kind, producerOptions });
        await this.pvcCall.hotswapProducer(kind, this.streamName, producerOptions);
    }
    toJSON() {
        return {
            msc: this.controller,
            stream: this.stream,
            options: this.options,
            aggregates: {
                ...(0, log_node_1.extractAggregates)(this.stream, "support"),
                support: this.ctx.support.hash,
                state: this.state,
            },
        };
    }
}
exports.Broadcast = Broadcast;
Broadcast.displayName = "Broadcast";
//# sourceMappingURL=broadcast.js.map